// Inventory / Weapon Balance screen.
// Shows every weapon in the catalogue with damage / fire-rate / range /
// magazine stats + comparative bars, so players can pick their fit before
// deploying. When new weapons are added to /game/weapons/weapons.js they
// appear here automatically.
import { useState } from "react";
import { useGameStore, SCREEN } from "@/stores/gameStore";
import { WEAPONS, WEAPON_ORDER } from "@/game/weapons/weapons";
import { AGENTS, AGENT_ORDER } from "@/game/agents/agents";
import { useSettings } from "@/stores/settingsStore";
import { PANEL } from "@/constants/testIds";
import LobbyLayout from "@/components/LobbyLayout";
import { X, Target, Timer, Ruler, Layers, Users, Crosshair, Heart, Zap, Volume2, RotateCw } from "lucide-react";

// Comparative max values (used to scale the visual bars).
// Update if you add a weapon that exceeds any of these.
const MAX = {
    damage: 60,
    fireRatePerMin: 900,
    range: 100,
    magSize: 40,
};

function fireRatePerMin(cooldownMs) {
    return Math.round(60000 / cooldownMs);
}

export default function InventoryScreen() {
    const goto = useGameStore((s) => s.goto);
    const [tab, setTab] = useState("weapons");
    const [selected, setSelected] = useState(WEAPON_ORDER[0]);
    const w = WEAPONS[selected];

    return (
        <LobbyLayout active={SCREEN.INVENTORY}>
        <div
            data-testid="inventory-screen"
            className="w-full h-full text-white flex flex-col overflow-hidden"
        >
            {/* Header */}
            <div className="flex items-center justify-between px-8 py-4">
                <div>
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-orange-300/80">
                        {tab === "weapons" ? "WEAPON BALANCE" : "AGENT BALANCE"}
                    </div>
                    <div className="axis-heading text-3xl tracking-[0.4em]">INVENTORY</div>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 mr-3 p-1 border border-white/10 axis-cut-sm bg-white/[0.02]">
                        <button
                            data-testid="inventory-tab-weapons"
                            onClick={() => setTab("weapons")}
                            className={`axis-cut-sm px-4 py-1.5 axis-mono text-[10px] tracking-[0.4em] transition ${
                                tab === "weapons"
                                    ? "bg-cyan-400/15 text-cyan-200 border border-cyan-400/60"
                                    : "text-white/50 border border-transparent hover:text-white/80"
                            }`}
                        >
                            WEAPONS
                        </button>
                        <button
                            data-testid="inventory-tab-agents"
                            onClick={() => setTab("agents")}
                            className={`axis-cut-sm px-4 py-1.5 axis-mono text-[10px] tracking-[0.4em] transition ${
                                tab === "agents"
                                    ? "bg-orange-400/15 text-orange-200 border border-orange-400/60"
                                    : "text-white/50 border border-transparent hover:text-white/80"
                            }`}
                        >
                            AGENTS
                        </button>
                    </div>
                    <button
                        data-testid={PANEL.close}
                        onClick={() => goto(SCREEN.MENU)}
                        className="axis-btn axis-btn-ghost axis-cut-sm px-4 py-2 text-xs flex items-center gap-2"
                    >
                        <X size={14} /> Close
                    </button>
                </div>
            </div>

            {tab === "weapons" ? (
            <div className="flex-1 overflow-hidden flex">
                {/* Weapon list */}
                <div className="w-72 border-r border-white/10 p-4 space-y-2 overflow-y-auto">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-white/40 px-2 mb-1">
                        LOADOUT
                    </div>
                    {WEAPON_ORDER.map((id) => {
                        const weapon = WEAPONS[id];
                        const active = selected === id;
                        return (
                            <button
                                key={id}
                                data-testid={`inventory-weapon-${id}`}
                                onClick={() => setSelected(id)}
                                className={`w-full axis-cut-sm px-3 py-3 text-left border transition ${
                                    active
                                        ? "bg-cyan-400/10 border-cyan-400"
                                        : "border-white/10 bg-white/[0.02] hover:border-cyan-400/60"
                                }`}
                            >
                                <div className="flex items-center justify-between mb-1">
                                    <span
                                        className="axis-mono text-[10px] tracking-[0.3em] px-1.5 py-px border"
                                        style={{
                                            borderColor: active
                                                ? weapon.model.sightColor
                                                : "rgba(255,255,255,0.2)",
                                            color: active
                                                ? weapon.model.sightColor
                                                : "rgba(255,255,255,0.5)",
                                        }}
                                    >
                                        SLOT {weapon.slot}
                                    </span>
                                    <span className="axis-mono text-[9px] tracking-[0.3em] text-white/40">
                                        {id.toUpperCase()}
                                    </span>
                                </div>
                                <div className="axis-heading text-sm tracking-widest">
                                    {weapon.displayName}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Detail panel */}
                <div className="flex-1 overflow-y-auto p-10">
                    <div className="max-w-4xl">
                        <div className="flex items-baseline gap-3 mb-2">
                            <span
                                className="axis-mono text-[10px] tracking-[0.4em] px-2 py-0.5 border"
                                style={{
                                    borderColor: w.model.sightColor,
                                    color: w.model.sightColor,
                                }}
                            >
                                SLOT {w.slot} · KEY {w.slot}
                            </span>
                            <span className="axis-mono text-[10px] tracking-[0.3em] text-white/40">
                                CLASS · {w.id.toUpperCase()}
                            </span>
                        </div>
                        <div className="axis-heading text-4xl tracking-[0.3em] axis-metallic mb-6">
                            {w.displayName}
                        </div>

                        {/* Visual weapon preview */}
                        <WeaponPreview weapon={w} />

                        {/* Stat cards */}
                        <div
                            data-testid="weapon-stats"
                            className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3"
                        >
                            <StatCard
                                icon={Target}
                                label="Damage"
                                value={w.damage}
                                max={MAX.damage}
                                color="#00e5ff"
                            />
                            <StatCard
                                icon={Timer}
                                label="Fire Rate"
                                value={fireRatePerMin(w.fireCooldownMs)}
                                max={MAX.fireRatePerMin}
                                suffix=" RPM"
                                color="#ff7043"
                            />
                            <StatCard
                                icon={Ruler}
                                label="Range"
                                value={w.range}
                                max={MAX.range}
                                suffix="m"
                                color="#ffb300"
                            />
                            <StatCard
                                icon={Layers}
                                label="Magazine"
                                value={w.magSize}
                                max={MAX.magSize}
                                sub={`${w.reserve} reserve`}
                                color="#00ff88"
                            />
                        </div>

                        {/* Comparison bars — same axis across all weapons */}
                        <div className="mt-10">
                            <div className="axis-mono text-[10px] tracking-[0.5em] text-white/40 mb-3">
                                COMPARE
                            </div>
                            <div className="space-y-2">
                                <CompareRow label="Damage" max={MAX.damage} suffix="">
                                    {WEAPON_ORDER.map((id) => (
                                        <CompareBar
                                            key={id}
                                            weapon={WEAPONS[id]}
                                            value={WEAPONS[id].damage}
                                            max={MAX.damage}
                                            active={id === selected}
                                        />
                                    ))}
                                </CompareRow>
                                <CompareRow label="Fire Rate" max={MAX.fireRatePerMin} suffix=" RPM">
                                    {WEAPON_ORDER.map((id) => (
                                        <CompareBar
                                            key={id}
                                            weapon={WEAPONS[id]}
                                            value={fireRatePerMin(WEAPONS[id].fireCooldownMs)}
                                            max={MAX.fireRatePerMin}
                                            active={id === selected}
                                        />
                                    ))}
                                </CompareRow>
                                <CompareRow label="Range" max={MAX.range} suffix="m">
                                    {WEAPON_ORDER.map((id) => (
                                        <CompareBar
                                            key={id}
                                            weapon={WEAPONS[id]}
                                            value={WEAPONS[id].range}
                                            max={MAX.range}
                                            active={id === selected}
                                        />
                                    ))}
                                </CompareRow>
                                <CompareRow label="Magazine" max={MAX.magSize} suffix="">
                                    {WEAPON_ORDER.map((id) => (
                                        <CompareBar
                                            key={id}
                                            weapon={WEAPONS[id]}
                                            value={WEAPONS[id].magSize}
                                            max={MAX.magSize}
                                            active={id === selected}
                                        />
                                    ))}
                                </CompareRow>
                            </div>
                        </div>

                        <div className="mt-8 axis-mono text-[10px] tracking-[0.3em] text-white/40">
                            IN-MATCH · PRESS <span className="text-white/70">1 / 2 / 3</span> TO
                            SWAP · <span className="text-white/70">R</span> TO RELOAD
                        </div>
                    </div>
                </div>
            </div>
            ) : (
                <AgentBalanceTab />
            )}
        </div>
        </LobbyLayout>
    );
}

function AgentBalanceTab() {
    const selectedAgent = useSettings((s) => s.selectedAgent) || "recruit";
    const update = useSettings((s) => s.update);
    const [focus, setFocus] = useState(selectedAgent);

    const equip = (id) => {
        update({ selectedAgent: id });
        setFocus(id);
    };

    // Comparative maxima for the visual bars
    const MAXES = {
        maxHp: 130,
        moveSpeedMult: 1.15,
        reloadSpeedMult: 1.15,
        stealth: 1.5, // inverted footstep vol below
        headshot: 1.5,
    };

    return (
        <div
            data-testid="agent-balance-tab"
            className="flex-1 overflow-y-auto px-8 pb-10"
        >
            <div className="max-w-6xl mx-auto">
                <div className="axis-mono text-[10px] tracking-[0.5em] text-white/50 mb-3">
                    OPERATORS · PICK YOUR PLAYSTYLE
                </div>

                {/* Agent tiles */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    {AGENT_ORDER.map((id) => {
                        const a = AGENTS[id];
                        const isFocus = focus === id;
                        const isEquipped = selectedAgent === id;
                        return (
                            <button
                                key={id}
                                data-testid={`agent-tile-${id}`}
                                onClick={() => setFocus(id)}
                                onDoubleClick={() => equip(id)}
                                className={`text-left axis-cut-sm border p-4 transition ${
                                    isFocus
                                        ? "bg-orange-400/10 border-orange-400"
                                        : "bg-white/[0.02] border-white/10 hover:border-orange-400/60"
                                }`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span
                                        className="axis-mono text-[9px] tracking-[0.4em] px-2 py-0.5 border axis-cut-sm"
                                        style={{
                                            borderColor: a.palette.beltAccent,
                                            color: a.palette.beltAccent,
                                        }}
                                    >
                                        {a.tag}
                                    </span>
                                    {isEquipped && (
                                        <span className="axis-mono text-[9px] tracking-[0.3em] text-cyan-300/80">
                                            EQUIPPED
                                        </span>
                                    )}
                                </div>
                                <div className="axis-heading text-lg tracking-widest">{a.name}</div>
                                <div className="axis-mono text-[10px] text-white/50 mt-1 leading-relaxed line-clamp-2">
                                    {a.passive.perkTitle}
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Focused agent detail */}
                <FocusedAgent id={focus} equipped={selectedAgent === focus} onEquip={() => equip(focus)} />

                {/* Comparison Table */}
                <div className="mt-8">
                    <div className="axis-mono text-[10px] tracking-[0.5em] text-white/40 mb-3">
                        SIDE-BY-SIDE · PERK BALANCE
                    </div>
                    <div
                        data-testid="agent-comparison"
                        className="axis-cut-sm border border-white/10 bg-white/[0.02] overflow-hidden"
                    >
                        <table className="w-full text-left">
                            <thead>
                                <tr className="axis-mono text-[10px] tracking-[0.4em] text-white/40 border-b border-white/10">
                                    <th className="px-4 py-3">STAT</th>
                                    {AGENT_ORDER.map((id) => (
                                        <th key={id} className="px-4 py-3 text-center">
                                            {AGENTS[id].name}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <StatRow
                                    icon={Heart}
                                    label="Max HP"
                                    color="#ff5060"
                                    getValue={(a) => a.passive.maxHp ?? 100}
                                    max={MAXES.maxHp}
                                    focus={focus}
                                />
                                <StatRow
                                    icon={Zap}
                                    label="Move Speed"
                                    color="#00e5ff"
                                    getValue={(a) => a.passive.moveSpeedMult ?? 1}
                                    max={MAXES.moveSpeedMult}
                                    fmt={(v) => `${Math.round(v * 100)}%`}
                                    focus={focus}
                                />
                                <StatRow
                                    icon={RotateCw}
                                    label="Reload Speed"
                                    color="#00ff88"
                                    getValue={(a) => a.passive.reloadSpeedMult ?? 1}
                                    max={MAXES.reloadSpeedMult}
                                    fmt={(v) => `${Math.round(v * 100)}%`}
                                    focus={focus}
                                />
                                <StatRow
                                    icon={Volume2}
                                    label="Stealth"
                                    color="#c084fc"
                                    // Lower footstep vol = higher stealth, so invert
                                    getValue={(a) => 2 - (a.passive.footstepVolumeMult ?? 1)}
                                    max={MAXES.stealth + 0.5}
                                    fmt={(v) => `${Math.round(v * 50)}%`}
                                    focus={focus}
                                />
                                <StatRow
                                    icon={Crosshair}
                                    label="Headshot Bonus"
                                    color="#ffb300"
                                    getValue={(a) => a.passive.headshotBonus ?? 1}
                                    max={MAXES.headshot}
                                    fmt={(v) => `${Math.round(v * 100)}%`}
                                    focus={focus}
                                />
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="mt-4 axis-mono text-[10px] tracking-[0.3em] text-white/40">
                    DOUBLE-CLICK A TILE OR PRESS EQUIP TO SET YOUR ACTIVE OPERATOR
                </div>
            </div>
        </div>
    );
}

function FocusedAgent({ id, equipped, onEquip }) {
    const a = AGENTS[id];
    if (!a) return null;
    return (
        <div
            data-testid={`agent-focus-${id}`}
            className="axis-cut-sm border border-white/10 p-6 grid md:grid-cols-[280px_1fr] gap-6"
            style={{
                background: `linear-gradient(135deg, ${a.palette.vest}22 0%, rgba(0,0,0,0.5) 100%)`,
            }}
        >
            {/* Silhouette */}
            <div className="flex items-center justify-center">
                <svg viewBox="0 0 120 200" width="200" height="220">
                    {/* Body */}
                    <rect x="34" y="52" width="52" height="70" rx="6" fill={a.palette.vest} />
                    <rect x="34" y="112" width="52" height="60" rx="4" fill={a.palette.trousers} />
                    <rect x="34" y="52" width="52" height="10" fill={a.palette.beltAccent} opacity="0.75" />
                    {/* Head */}
                    <circle cx="60" cy="34" r="18" fill={a.palette.skin} />
                    <path d="M 42 30 Q 60 12 78 30 L 78 22 Q 60 6 42 22 Z" fill={a.palette.hair} />
                    {/* Shoulders */}
                    <rect x="20" y="56" width="16" height="46" rx="6" fill={a.palette.shirt} />
                    <rect x="84" y="56" width="16" height="46" rx="6" fill={a.palette.shirt} />
                    {/* Gloves */}
                    <circle cx="28" cy="106" r="7" fill={a.palette.gloves} />
                    <circle cx="92" cy="106" r="7" fill={a.palette.gloves} />
                    {/* Boots */}
                    <rect x="40" y="172" width="18" height="18" fill={a.palette.boots} />
                    <rect x="62" y="172" width="18" height="18" fill={a.palette.boots} />
                </svg>
            </div>
            <div>
                <div className="flex items-center gap-3 mb-2">
                    <span
                        className="axis-mono text-[10px] tracking-[0.4em] px-2 py-0.5 border axis-cut-sm"
                        style={{ borderColor: a.palette.beltAccent, color: a.palette.beltAccent }}
                    >
                        {a.tag}
                    </span>
                    <span className="axis-mono text-[10px] tracking-[0.3em] text-white/40">
                        CLASS · {a.id.toUpperCase()}
                    </span>
                </div>
                <div className="axis-heading text-3xl tracking-[0.3em] axis-metallic">{a.name}</div>
                <div className="axis-mono text-sm text-white/70 mt-3 leading-relaxed">
                    {a.description}
                </div>

                <div className="mt-4 axis-cut-sm border border-white/10 bg-black/40 p-3">
                    <div className="axis-mono text-[10px] tracking-[0.4em] text-cyan-300/80">
                        PERK · {a.passive.perkTitle}
                    </div>
                    <div className="axis-mono text-sm text-white/80 mt-1">{a.passive.perkDesc}</div>
                </div>

                <div className="mt-4 flex items-center gap-3">
                    <button
                        data-testid={`agent-equip-${id}`}
                        onClick={onEquip}
                        disabled={equipped}
                        className={`axis-btn axis-cut-sm px-5 py-2 text-xs flex items-center gap-2 border ${
                            equipped
                                ? "border-white/10 text-white/40 cursor-default"
                                : "border-cyan-400 bg-cyan-500/15 text-cyan-200 hover:bg-cyan-500/30"
                        }`}
                    >
                        <Users size={13} /> {equipped ? "EQUIPPED" : "EQUIP OPERATOR"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function StatRow({ icon: Icon, label, color, getValue, max, fmt, focus }) {
    return (
        <tr className="border-b border-white/5 last:border-0">
            <td className="px-4 py-3 axis-mono text-[10px] tracking-[0.4em] text-white/60">
                <span className="flex items-center gap-2">
                    {Icon && <Icon size={12} style={{ color }} />}
                    {label}
                </span>
            </td>
            {AGENT_ORDER.map((id) => {
                const raw = getValue(AGENTS[id]);
                const pct = Math.max(6, Math.min(100, (raw / max) * 100));
                const active = id === focus;
                return (
                    <td key={id} className="px-4 py-3">
                        <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-white/5 axis-cut-sm overflow-hidden">
                                <div
                                    className="h-full transition-all duration-500"
                                    style={{
                                        width: `${pct}%`,
                                        background: active ? color : `${color}66`,
                                        boxShadow: active ? `0 0 8px ${color}88` : "none",
                                    }}
                                />
                            </div>
                            <span
                                className={`axis-hud text-sm w-14 text-right ${
                                    active ? "text-white" : "text-white/45"
                                }`}
                            >
                                {fmt ? fmt(raw) : raw}
                            </span>
                        </div>
                    </td>
                );
            })}
        </tr>
    );
}

function StatCard({ icon: Icon, label, value, max, suffix = "", sub, color }) {
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div className="axis-cut-sm border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center gap-2 mb-1">
                {Icon && <Icon size={12} style={{ color }} />}
                <span className="axis-mono text-[10px] tracking-[0.4em] text-white/50">
                    {label}
                </span>
            </div>
            <div className="axis-hud text-4xl leading-none" style={{ color }}>
                {value}
                <span className="axis-mono text-sm text-white/40 ml-1">{suffix}</span>
            </div>
            {sub && (
                <div className="axis-mono text-[9px] tracking-[0.3em] text-white/40 mt-1">{sub}</div>
            )}
            <div className="mt-3 h-1 bg-white/10 axis-cut-sm overflow-hidden">
                <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
        </div>
    );
}

function CompareRow({ label, children }) {
    return (
        <div className="axis-cut-sm border border-white/10 bg-white/[0.02] p-3">
            <div className="axis-mono text-[10px] tracking-[0.4em] text-white/50 mb-2">
                {label}
            </div>
            <div className="space-y-1.5">{children}</div>
        </div>
    );
}

function CompareBar({ weapon, value, max, active }) {
    const pct = Math.min(100, (value / max) * 100);
    return (
        <div className="flex items-center gap-3">
            <span
                className={`axis-mono text-[10px] tracking-[0.3em] w-20 ${
                    active ? "text-white" : "text-white/45"
                }`}
            >
                {weapon.id.toUpperCase()}
            </span>
            <div className="flex-1 h-1.5 bg-white/5 axis-cut-sm overflow-hidden">
                <div
                    className="h-full transition-all duration-500"
                    style={{
                        width: `${pct}%`,
                        background: active ? weapon.model.sightColor : "rgba(255,255,255,0.35)",
                        boxShadow: active
                            ? `0 0 10px ${weapon.model.sightColor}88`
                            : "none",
                    }}
                />
            </div>
            <span
                className={`axis-hud text-lg w-16 text-right ${active ? "text-white" : "text-white/50"}`}
            >
                {value}
            </span>
        </div>
    );
}

// A simple SVG weapon silhouette using the model color palette.
function WeaponPreview({ weapon }) {
    // Draw a small side-profile shape scaled by body length.
    const bodyLenPx = weapon.model.bodySize[2] * 260 + weapon.model.barrelLen * 260;
    return (
        <div
            className="axis-cut-sm border border-white/10 p-6"
            style={{
                background:
                    "linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(0,0,0,0.4) 100%)",
            }}
        >
            <div className="flex items-center justify-center" style={{ height: 140 }}>
                <svg
                    width={Math.max(240, bodyLenPx + 60)}
                    height="140"
                    viewBox={`0 0 ${Math.max(240, bodyLenPx + 60)} 140`}
                >
                    {/* Barrel */}
                    <rect
                        x={30}
                        y={62}
                        width={weapon.model.barrelLen * 260}
                        height={8}
                        fill={weapon.model.barrelColor}
                    />
                    {/* Body */}
                    <rect
                        x={30 + weapon.model.barrelLen * 260}
                        y={50}
                        width={weapon.model.bodySize[2] * 260}
                        height={28}
                        fill={weapon.model.color}
                    />
                    {/* Sight (glow) */}
                    <rect
                        x={30 + weapon.model.barrelLen * 260 + 20}
                        y={38}
                        width={22}
                        height={12}
                        fill={weapon.model.sightColor}
                        opacity="0.85"
                    />
                    <rect
                        x={30 + weapon.model.barrelLen * 260 + 22}
                        y={34}
                        width={18}
                        height={4}
                        fill={weapon.model.sightColor}
                        opacity="0.35"
                    />
                    {/* Grip */}
                    <path
                        d={`M ${30 + weapon.model.barrelLen * 260 + weapon.model.bodySize[2] * 260 * 0.6} 78 l 8 32 l -22 0 l -6 -18 z`}
                        fill={weapon.model.gripColor}
                    />
                    {/* Magazine */}
                    <rect
                        x={30 + weapon.model.barrelLen * 260 + weapon.model.bodySize[2] * 260 * 0.3}
                        y={78}
                        width={16}
                        height={weapon.magSize * 0.7 + 6}
                        fill="#0a0d12"
                    />
                    {/* Sight glow ray */}
                    <line
                        x1={30 + weapon.model.barrelLen * 260 + 22}
                        y1={38}
                        x2={30 + weapon.model.barrelLen * 260 + 22}
                        y2={24}
                        stroke={weapon.model.sightColor}
                        strokeWidth="1"
                        opacity="0.6"
                    />
                </svg>
            </div>
            <div className="text-center axis-mono text-[10px] tracking-[0.4em] text-white/40 mt-2">
                {weapon.displayName}
            </div>
        </div>
    );
}
